import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from "@material-ui/core";
import { FormattedMessage, useIntl } from "react-intl";
import { Link as RouterLink } from "react-router-dom";

import Title from "../../ui/Title";
import { useBmapi } from "../../utils/bmapi-context";
import { getErrorMessageString } from "../../utils/errors";
import { MANAGER_ROUTES } from "../../utils/constants";
import { ArrowBack } from "@material-ui/icons";
import { common } from "../../messages";

export default function ManageSync() {
  const { bmapi, startLoading, stopLoading, notifyError } = useBmapi();
  const intl = useIntl();

  const [syncConfig, setSyncConfig] = useState(null);

  // { [name]: { counting: bool, error: string|null, count: number|null } }
  const [counts, setCounts] = useState({});
  // { [name]: { synchronizing: bool, error: string|null } }

  useEffect(() => {
    if (!bmapi?.getSyncConfig) return;

    startLoading?.();

    bmapi
      .getSyncConfig()
      .then((resp) => setSyncConfig(resp || null))
      .catch((e) => notifyError?.(getErrorMessageString(e, intl)))
      .finally(() => stopLoading?.());
  }, [bmapi, startLoading, stopLoading, notifyError, intl]);

  const syncType = (syncConfig?.sync_type ?? "").trim();
  const hasSyncType = syncType.length > 0;
  const entities = Array.isArray(syncConfig?.entities)
    ? syncConfig.entities
    : [];

  const handleCount = useCallback(
    (entityName) => {
      const name = (entityName ?? "").trim();
      if (!name) return;

      setCounts((prev) => ({
        ...prev,
        [name]: { counting: true, error: null, count: null },
      }));

      bmapi
        .getSyncCount(name)
        .then((count) => {
          const n = Number(count);

          setCounts((prev) => ({
            ...prev,
            [name]: {
              counting: false,
              error: null,
              count: Number.isFinite(n) ? n : null,
            },
          }));
        })
        .catch((e) => {
          console.error("getSyncCount failed", { entity: name, error: e });

          const msgRaw = getErrorMessageString(e, intl);
          const msg =
            typeof msgRaw === "string" && msgRaw.trim().length > 0
              ? msgRaw.trim()
              : "Errore durante il conteggio.";

          setCounts((prev) => ({
            ...prev,
            [name]: {
              counting: false,
              error: msg,
              count: null,
            },
          }));
        });
    },
    [bmapi, intl]
  );

  return (
    <Container maxWidth="md">
      <Box mt={2} mb={2}>
        <Button
          component={RouterLink}
          to={MANAGER_ROUTES.HOME}
          startIcon={<ArrowBack />}
        >
          {intl.formatMessage(common.backHome)}
        </Button>
      </Box>

      <Title>
        <FormattedMessage
          id="navigation.manager.sync"
          defaultMessage="Sincronizzazione"
        />
      </Title>

      <Box mt={2}>
        {hasSyncType ? (
          <Typography variant="body1">
            <strong>Tipo di sincronizzazione:</strong> {syncType}
          </Typography>
        ) : (
          <Typography variant="body1">Configurazione non presente</Typography>
        )}
      </Box>

      {entities.length > 0 && (
        <Box mt={3}>
          <Typography variant="h6">Entità</Typography>

          <List>
            {entities.map((entity, idx) => {
              const name = (entity?.name ?? "").trim();
              const countRow = counts[name] || {
                counting: false,
                error: null,
                count: null,
              };

              return (
                <React.Fragment key={`${name || "entity"}-${idx}`}>
                  <ListItem divider>
                    <ListItemText primary={name} />

                    {/* Area destra: count a sinistra, bottone a destra */}
                    <Box display="flex" alignItems="center">
                      {typeof countRow.count === "number" && (
                        <Box mr={2}>
                          <Typography variant="body2">
                            Record letti: <strong>{countRow.count}</strong>
                          </Typography>
                        </Box>
                      )}

                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        disabled={!name || countRow.counting}
                        onClick={() => handleCount(name)}
                        startIcon={
                          countRow.counting ? (
                            <CircularProgress size={16} />
                          ) : null
                        }
                      >
                        Conta
                      </Button>
                    </Box>
                  </ListItem>

                  {countRow.error && (
                    <Box px={2} pb={1}>
                      <Typography variant="body2" style={{ color: "red" }}>
                        {countRow.error}
                      </Typography>
                    </Box>
                  )}
                </React.Fragment>
              );
            })}
          </List>
        </Box>
      )}
    </Container>
  );
}
